import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * How hard the window lands. `notice` keeps the dashboard usable and labels
 * what it cannot show; `blocked` replaces the telemetry-backed views with a
 * single explanation. Support stays reachable in both.
 */
export type MaintenanceMode = "notice" | "blocked";

/**
 * A maintenance window announced for one product (CTX-483).
 *
 * `since` is the window start. It is the only timestamp the UI can trust:
 * once the collector is unreachable there is no way to recover when the last
 * real reading happened, so "no data since 22:00" is an honest statement
 * while "last reading 22:04" would be invented.
 */
export interface MaintenanceWindow {
  id: string;
  /** Canonical product name from the API ("Kontrol Edge", "Reflector", ...). */
  product: string;
  mode: MaintenanceMode;
  /** Ready to print: the operator's text, or the platform default. */
  message: string;
  /**
   * The same text, but null when nobody wrote one.
   *
   * The overview's Maintenance row needs the difference: it composes its
   * own sentence (switching tense when the window opens, carrying the
   * reader's local times) and may only override that with copy a human
   * actually authored — never with the default, which is three sentences
   * long and written for a banner.
   */
  customMessage: string | null;
  since: string;
  until: string;
}

export interface MaintenancePayload {
  byProduct: Record<string, MaintenanceWindow>;
  upcomingByProduct: Record<string, MaintenanceWindow>;
}

interface MaintenanceState {
  /** Keyed by the API product name. Empty when nothing is open. */
  byProduct: Record<string, MaintenanceWindow>;
  /**
   * Announced but not yet started, keyed the same way.
   *
   * Kept apart from `byProduct` rather than merged, because everything that
   * reacts to a window — the banner, the blocked view, the "no data" labels
   * — reads `byProduct`, and a window scheduled for next week must not make
   * any of that happen today. This set is only ever announced.
   */
  upcomingByProduct: Record<string, MaintenanceWindow>;
  active: boolean;
  isLoading: boolean;
}

const initialState: MaintenanceState = {
  byProduct: {},
  upcomingByProduct: {},
  active: false,
  isLoading: false,
};

export const maintenanceSlice = createSlice({
  name: "maintenance",
  initialState,
  reducers: {
    setMaintenance: (
      state,
      action: PayloadAction<Partial<MaintenancePayload>>
    ) => {
      // Defaulted rather than required: this reducer runs against whatever
      // the API returned, and a backend that predates `upcomingByProduct`
      // would otherwise take the dashboard down with it. The one thing that
      // must not break during maintenance is the notice about it.
      const byProduct = action.payload?.byProduct ?? {};

      state.byProduct = byProduct;
      state.upcomingByProduct = action.payload?.upcomingByProduct ?? {};
      state.active = Object.keys(byProduct).length > 0;
      state.isLoading = false;
    },
    // A failed poll must not clear a window that is still open: the request
    // can fail for reasons that have nothing to do with the window, and
    // dropping the notice mid-maintenance is the opposite of what it is for.
    maintenanceCheckFailed: (state) => {
      state.isLoading = false;
    },
    setMaintenanceLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    clearMaintenance: () => initialState,
  },
});

export const {
  setMaintenance,
  maintenanceCheckFailed,
  setMaintenanceLoading,
  clearMaintenance,
} = maintenanceSlice.actions;

export default maintenanceSlice.reducer;
